import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import AttachmentIcon from '@atlaskit/icon/core/attachment';
import LinkIcon from '@atlaskit/icon/core/link';
import { fg } from '@atlaskit/platform-feature-flags';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { Expand, ExpandList } from '../../../../../../../common/ui/expand-list';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { getLikeCount } from '../../../../../../../common/ui/like-count';
import {
	getLinksFromText,
	LinkAttachments,
	buttonLabel as linkAttachmentsButtonLabel,
} from '../../../../../../../common/ui/link-attachments';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { getTopReaction } from '../../../../../../../common/ui/top-reaction';
// eslint-disable-next-line  @atlassian/tangerine/import/no-parent-imports
import { ThreadBody } from '../../common/thread-body';

import type { searchGraphMessage_AtlassianSearchPage$key } from './__generated__/searchGraphMessage_AtlassianSearchPage.graphql';
import { DropdownItems } from './attachments/attachment-items';
import { messages as i18n } from './messages';
import { ThreadTitle } from './thread-title';

/**
 * Returns links for a search result, using one of two strategies:
 * - If `data.links` is an Array (from GraphQL), use those links directly.
 * - If `data.links` is null/undefined (GraphQL didn't provide them), fall back to
 *   parsing links from excerpt text on the frontend.
 *
 * @param data - The search result data containing links from GraphQL schema
 * @param linkedEntities - Optional linked entities that may contain links in their excerpts
 * @returns Array of links
 */
export const getLinks = (
	data: {
		links?: readonly string[] | null;
		excerpt?: string | null;
		entity?: { description?: string | null } | null;
	},
	linkedEntities?:
		| readonly { excerpt?: string | null; entity?: { description?: string | null } | null }[]
		| null,
): string[] => {
	// If GraphQL provided links (even an empty array), use them directly
	if (Array.isArray(data.links)) {
		return [...data.links];
	}

	// Otherwise, fall back to parsing links from text (pre-existing approach)
	return (
		[data, ...(linkedEntities ?? [])]
			?.map((node) => node.entity?.description || node.excerpt)
			?.filter((excerpt): excerpt is string => !!excerpt)
			?.flatMap(getLinksFromText) ?? []
	);
};

export type SearchGraphMessageProps =
	SearchGraphResultProps<searchGraphMessage_AtlassianSearchPage$key>;

export const SearchGraphMessage = ({
	node,
	position,
	cloudId,
	primaryProduct,
	primarySubProduct,
	selectedProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	thirdPartyConfigs,
	mainContentWidth,
}: SearchGraphMessageProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchGraphMessage_AtlassianSearchPage on SearchResultGraphDocument
			@argumentDefinitions(
				includeSlackTopReaction: {
					type: "Boolean!"
					provider: "./is-slack-top-reaction-enabled.relayprovider"
				}
			) {
				...attachmentItems_AtlassianSearchPage_DropdownItems
				...attachments_AtlassianSearchPage
				...threadTitle_AtlassianSearchPage
				...threadBody_AtlassianSearchPage
				id
				integrationId
				url
				excerpt
				links
				topReaction @include(if: $includeSlackTopReaction) {
					emojiName
					emojiUrl
					count
				}
				attachments {
					__typename
				}
				linkedEntities {
					excerpt
					entity {
						... on ExternalMessage {
							description
						}
					}
				}
				linkedEntitiesType
				lastModifiedDate
				entity {
					... on ExternalMessage {
						lastActive
						reactionCount
						description
						attachments {
							__typename
						}
						container @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalConversation {
								id
								memberCount
								membershipType
							}
						}
					}
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const { formatMessage } = useIntl();

	const { url, linkedEntities = [] } = data;

	// Get attachments count - prefer GraphQL schema attachments when available,
	// otherwise fall back to entity attachments
	const graphqlAttachments = data.attachments ?? [];
	const entityAttachments = data.entity?.attachments || [];
	const attachments = graphqlAttachments.length > 0 ? graphqlAttachments : entityAttachments;

	// Use linkedEntitiesType if available, otherwise fallback to existing logic
	const labelMessage = data.linkedEntitiesType
		? data.linkedEntitiesType === 'thread'
			? i18n.threadTypeLabel
			: i18n.messageTypeLabel
		: linkedEntities?.length
			? i18n.threadTypeLabel
			: i18n.messageTypeLabel;

	const title = <ThreadTitle node={data} thirdPartyConfigs={thirdPartyConfigs} />;
	const body = <ThreadBody node={data} />;
	const parts: Array<string | [string, React.ReactNode]> = [
		['label', <FormattedMessage {...labelMessage} />],
	];
	const socialParts: Array<string | [string, React.ReactNode]> = [];

	const conversationMemberCount = data.entity?.container?.memberCount;
	if (conversationMemberCount) {
		parts.push([
			'memberCount',
			<FormattedMessage {...i18n.memberCount} values={{ count: conversationMemberCount }} />,
		]);
	}
	const lastActiveDate = data?.entity?.lastActive || data?.lastModifiedDate;
	if (lastActiveDate) {
		parts.push([
			'lastActive',
			<LastModified lastModified={lastActiveDate} message={i18n.lastActive} />,
		]);
	}

	// Keep teams/legacy reaction pathway exactly as in master.
	if (fg('rovo_fps_enable_3p_messages_reaction_count_ui')) {
		const likeCountComponent = getLikeCount(data.entity?.reactionCount);
		if (likeCountComponent) {
			socialParts.push(['like-count', likeCountComponent]);
		}
	}

	// Additive slack-only top reaction path.
	const isSlackMessage = data.integrationId?.includes('integration/slack');
	if (isSlackMessage && fg('rovo_fps_show_slack_top_reaction_in_search')) {
		const topReactionComponent = getTopReaction({
			emojiName: data.topReaction?.emojiName,
			emojiUrl: data.topReaction?.emojiUrl,
			count: data.topReaction?.count,
		});
		if (topReactionComponent) {
			parts.push(['slack-top-reaction', topReactionComponent]);
		}
	}

	const links = getLinks(data, linkedEntities);

	const shouldShowAttachmentsOrLinks = attachments.length > 0 || links.length > 0;
	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const shouldShowRelatedLinks = linkedResults && linkedResults.length > 0;

	return (
		<SearchResult
			id={data.id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			icon={icon}
			description={body}
			headerMetadataParts={parts}
			headerMetadataSocialParts={socialParts}
			footer={
				<>
					{shouldShowRelatedLinks ? (
						<MentionedByLinkedResults
							linkedResults={linkedResults || []}
							thirdPartyConfigs={thirdPartyConfigs}
							partial={data}
							url={url}
							onResultClicked={onResultClicked}
							position={position}
							onResultContextMenu={onResultContextMenu}
							isAllProductSelected={!selectedProduct}
						/>
					) : (
						shouldShowAttachmentsOrLinks && (
							<ExpandList>
								<Expand
									icon={AttachmentIcon}
									attachmentCount={attachments.length}
									attachmentType="file"
									label={formatMessage(i18n.fileAttachmentsExpandButtonLabel, {
										count: attachments.length,
									})}
								>
									<DropdownItems node={data} product={product} />
								</Expand>
								<Expand
									icon={LinkIcon}
									attachmentCount={links.length}
									attachmentType="url"
									label={formatMessage(linkAttachmentsButtonLabel, { count: links.length })}
								>
									<LinkAttachments links={links} onResultClicked={onResultClicked} />
								</Expand>
							</ExpandList>
						)
					)}
				</>
			}
			title={title}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			position={position}
			appMode={appMode}
			isPrivate={data?.entity?.container?.membershipType === 'PRIVATE'}
			hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
			mainContentWidth={mainContentWidth}
		/>
	);
};
